import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Copy, KeyRound, Eye, EyeOff, ExternalLink, Building2, Save, DollarSign } from "lucide-react";

/**
 * Admin → SSO config
 * Shows the parent-origin allowlist + the shared secret (masked).
 * Super admin can reveal the secret once (password-gated) to paste it into
 * the parent portal so it can mint SSO JWTs.
 */
export default function AdminSSO() {
  const [config, setConfig] = useState(null);
  const [commissionsConfig, setCommissionsConfig] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [revealPassword, setRevealPassword] = useState("");
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [revealedCommSecret, setRevealedCommSecret] = useState(null);
  const [revealCommPassword, setRevealCommPassword] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [revealingComm, setRevealingComm] = useState(false);
  const [parentOriginDraft, setParentOriginDraft] = useState("");
  const [commOriginDraft, setCommOriginDraft] = useState("");
  const [parentSecretDraft, setParentSecretDraft] = useState("");
  const [commSecretDraft, setCommSecretDraft] = useState("");
  const [showParentSecret, setShowParentSecret] = useState(false);
  const [showCommSecret, setShowCommSecret] = useState(false);
  const [savingOrigins, setSavingOrigins] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const load = async () => {
    try {
      const [cRes, ccRes, aRes] = await Promise.all([
        api.get("/admin/sso/config"),
        api.get("/admin/sso/commissions-config"),
        api.get("/agencies"),
      ]);
      setConfig(cRes.data);
      setCommissionsConfig(ccRes.data);
      setParentOriginDraft(cRes.data?.parent_origin || "");
      setCommOriginDraft(ccRes.data?.commissions_origin || "");
      setAgencies(aRes.data || []);
    } catch (e) { toast.error("Failed to load SSO config"); }
  };
  useEffect(() => { load(); }, []);

  const reveal = async (e) => {
    e.preventDefault();
    if (!revealPassword.trim()) { toast.error("Enter your password to reveal"); return; }
    setRevealing(true);
    try {
      const { data } = await api.post("/admin/sso/config/reveal", { password: revealPassword });
      setRevealedSecret(data.secret);
      setRevealPassword("");
      toast.success("Parent secret revealed — copy it now");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reveal failed");
    } finally { setRevealing(false); }
  };

  const revealComm = async (e) => {
    e.preventDefault();
    if (!revealCommPassword.trim()) { toast.error("Enter your password to reveal"); return; }
    setRevealingComm(true);
    try {
      const { data } = await api.post("/admin/sso/commissions-config/reveal", { password: revealCommPassword });
      setRevealedCommSecret(data.secret);
      setRevealCommPassword("");
      toast.success("Commissions secret revealed — copy it now");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reveal failed");
    } finally { setRevealingComm(false); }
  };

  const saveOrigins = async () => {
    const payload = {};
    if ((parentOriginDraft || "").trim() !== (config?.parent_origin || "")) payload.parent_origin = parentOriginDraft.trim();
    if ((commOriginDraft || "").trim() !== (commissionsConfig?.commissions_origin || "")) payload.commissions_origin = commOriginDraft.trim();
    if (parentSecretDraft.trim()) payload.parent_secret = parentSecretDraft.trim();
    if (commSecretDraft.trim()) payload.commissions_secret = commSecretDraft.trim();
    if (Object.keys(payload).length === 0) { toast.info("No changes"); return; }
    setSavingOrigins(true);
    try {
      const { data } = await api.patch("/admin/sso/origins", payload);
      setConfig((c) => ({ ...c, parent_origin: data.parent_origin, has_secret: data.has_parent_secret }));
      setCommissionsConfig((c) => ({ ...c, commissions_origin: data.commissions_origin, has_secret: data.has_commissions_secret }));
      setParentSecretDraft("");
      setCommSecretDraft("");
      toast.success("SSO config saved — takes effect immediately");
      // Refresh masks
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSavingOrigins(false); }
  };

  // Client-side random secret generator — 32 bytes → 64 hex chars, HS256-strength.
  const genSecret = () => {
    const bytes = new Uint8Array(32);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const copy = async (val, label) => {
    if (!val) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const parentOrigin = config?.parent_origin || "https://your-parent-site.example.com";
  const backendOrigin = process.env.REACT_APP_BACKEND_URL || "https://your-app.example.com";
  const iframeSnippet = `<!-- 1. Embed the AWG portal -->
<iframe id="awg" src="${backendOrigin}"
        style="width:100%;height:100vh;border:0"
        allow="clipboard-write"></iframe>

<script>
  // 2. Wait for the iframe to announce it's ready
  window.addEventListener("message", (e) => {
    if (e.origin !== "${backendOrigin}") return;
    if (e.data?.type === "AWG_SSO_READY") sendToken();
  });

  // 3. Mint a short-lived JWT on your server (never in the browser)
  //    signed with the SSO_PARENT_SECRET below, then post it to the iframe.
  async function sendToken() {
    const res = await fetch("/api/awg-sso-token", { credentials: "include" });
    const { token } = await res.json();
    document.getElementById("awg").contentWindow.postMessage(
      { type: "AWG_SSO_TOKEN", token },
      "${backendOrigin}"
    );
  }
</script>`;

  const nodeSnippet = `// server-side, e.g. /api/awg-sso-token
const jwt = require("jsonwebtoken");
const AWG_SSO_SECRET = process.env.AWG_SSO_SECRET;   // paste from AWG admin

app.get("/api/awg-sso-token", (req, res) => {
  const user = req.session.user;                     // your logged-in user
  const token = jwt.sign({
    email: user.email,                               // required
    name: user.name,                                 // optional
    role: user.awgRole || "agent",                   // agent | agency_admin | admin
    agency_id: user.awgAgencyId,                     // optional — matches AWG agency._id
    // agency_name: "Test Motor Group",              // OR resolve by name
    // visibility_scope: "assigned",                 // optional
  }, AWG_SSO_SECRET, { algorithm: "HS256", expiresIn: "5m" });
  res.json({ token });
});`;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-sso-page">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Administration · Integrations</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] tracking-tight leading-none mt-2">
            SSO — Parent-site handoff
          </h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-2xl leading-relaxed">
            Embed this portal in <span className="font-mono text-[#0B132B]">{parentOrigin}</span> and sign your users in silently.
            Your parent server mints a short-lived JWT signed with the shared secret below and posts it to the iframe via
            <span className="font-mono text-[#0B132B]"> postMessage</span> — we auto-provision the user and drop them straight in.
          </p>
        </div>

        {/* White-label origins + shared secrets — editable */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-origins-card">
          <div className="flex items-start gap-3 mb-4">
            <Shield size={22} className="text-[#0B132B]"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Live SSO configuration</div>
              <div className="text-base font-bold text-[#0B132B]">Origins & shared secrets</div>
              <div className="text-[11px] text-[#6C757D] mt-1">
                Point AWG at your production hosts (e.g. <span className="font-mono">agentportal.awgadmin.com</span>, <span className="font-mono">commissions.awgadmin.com</span>) and rotate either shared secret. Persisted in the database — takes effect on the next page load, no redeploy needed. Env vars remain the bootstrap fallback.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Parent portal origin</Label>
              <Input value={parentOriginDraft} onChange={(e) => setParentOriginDraft(e.target.value)}
                placeholder="https://agentportal.awgadmin.com"
                className="mt-1 h-10 rounded-sm font-mono text-sm"
                data-testid="sso-parent-origin-input" />
              <div className="text-[10px] text-[#6C757D] mt-1">Whatever host will iframe AWG (drives CSP + SSO handoff).</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Commissions origin (white-labeled)</Label>
              <Input value={commOriginDraft} onChange={(e) => setCommOriginDraft(e.target.value)}
                placeholder="https://commissions.awgadmin.com"
                className="mt-1 h-10 rounded-sm font-mono text-sm"
                data-testid="sso-commissions-origin-input" />
              <div className="text-[10px] text-[#6C757D] mt-1">Where dealercomm is hosted — also drives the Commissions API URL.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E9ECEF]">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Parent shared secret</Label>
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#6C757D]">Current: {config?.secret_masked || "unset"} <span className="text-[#D4AF37]">· {config?.secret_source || "?"}</span></span>
              </div>
              <div className="flex gap-2 mt-1">
                <Input
                  type={showParentSecret ? "text" : "password"}
                  value={parentSecretDraft} onChange={(e) => setParentSecretDraft(e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="h-10 rounded-sm font-mono text-sm flex-1"
                  data-testid="sso-parent-secret-input" />
                <Button type="button" size="icon" variant="outline"
                  onClick={() => setShowParentSecret((s) => !s)}
                  className="h-10 w-10 rounded-sm border-[#0B132B]/20 shrink-0" title={showParentSecret ? "Hide" : "Show"}
                  data-testid="sso-parent-secret-toggle">
                  {showParentSecret ? <EyeOff size={12}/> : <Eye size={12}/>}
                </Button>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => { setParentSecretDraft(genSecret()); setShowParentSecret(true); }}
                  className="h-10 rounded-sm border-[#0B132B]/20 shrink-0" title="Generate a new 64-char secret"
                  data-testid="sso-parent-secret-gen">
                  <KeyRound size={12} className="mr-1"/> Rotate
                </Button>
              </div>
              <div className="text-[10px] text-[#6C757D] mt-1">Used to verify JWTs from the parent portal on <span className="font-mono">/auth/sso/parent</span>.</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Commissions shared secret</Label>
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#6C757D]">Current: {commissionsConfig?.secret_masked || "unset"} <span className="text-[#D4AF37]">· {commissionsConfig?.secret_source || "?"}</span></span>
              </div>
              <div className="flex gap-2 mt-1">
                <Input
                  type={showCommSecret ? "text" : "password"}
                  value={commSecretDraft} onChange={(e) => setCommSecretDraft(e.target.value)}
                  placeholder="Leave blank to keep current"
                  className="h-10 rounded-sm font-mono text-sm flex-1"
                  data-testid="sso-commissions-secret-input" />
                <Button type="button" size="icon" variant="outline"
                  onClick={() => setShowCommSecret((s) => !s)}
                  className="h-10 w-10 rounded-sm border-[#0B132B]/20 shrink-0" title={showCommSecret ? "Hide" : "Show"}
                  data-testid="sso-commissions-secret-toggle">
                  {showCommSecret ? <EyeOff size={12}/> : <Eye size={12}/>}
                </Button>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => { setCommSecretDraft(genSecret()); setShowCommSecret(true); }}
                  className="h-10 rounded-sm border-[#0B132B]/20 shrink-0" title="Generate a new 64-char secret"
                  data-testid="sso-commissions-secret-gen">
                  <KeyRound size={12} className="mr-1"/> Rotate
                </Button>
              </div>
              <div className="text-[10px] text-[#6C757D] mt-1">Used for the Commissions iframe SSO handoff <b>and</b> the server-to-server /api/ext/commissions/summary call. Must match dealercomm's <span className="font-mono">SSO_PARENT_SECRET</span>.</div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] text-[#6C757D]">
              {config?.parent_origin === parentOriginDraft && commissionsConfig?.commissions_origin === commOriginDraft && !parentSecretDraft && !commSecretDraft
                ? "No pending changes"
                : "Unsaved changes"}
            </div>
            <Button onClick={saveOrigins} disabled={savingOrigins}
              className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-bold"
              data-testid="sso-save-origins-btn">
              <Save size={12} className="mr-1.5"/> {savingOrigins ? "Saving…" : "Save configuration"}
            </Button>
          </div>
        </Card>

        {/* Parent origin card */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-parent-origin-card">
          <div className="flex items-start gap-3 mb-3">
            <Shield size={22} className="text-[#0B132B]"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Allowed parent origin</div>
              <div className="text-base font-bold text-[#0B132B]">{parentOrigin}</div>
              <div className="text-[11px] text-[#6C757D] mt-1">Set via <span className="font-mono">SSO_PARENT_ORIGIN</span> in backend/.env — CSP <span className="font-mono">frame-ancestors</span> is auto-scoped to this origin.</div>
            </div>
          </div>
        </Card>

        {/* Secret card */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-secret-card">
          <div className="flex items-start gap-3 mb-4">
            <KeyRound size={22} className="text-[#0B132B]"/>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Shared secret (HS256)</div>
              <div className="text-base font-mono text-[#0B132B] mt-1">{revealedSecret || config?.secret_masked || "unset"}</div>
              <div className="text-[11px] text-[#6C757D] mt-1">Sign parent-side JWTs with this. Keep it on your server — never expose to browsers.</div>
            </div>
            {revealedSecret && (
              <Button size="sm" variant="outline" onClick={() => copy(revealedSecret, "Secret")}
                className="rounded-sm h-9 border-[#0B132B]/20" data-testid="copy-sso-secret">
                <Copy size={12} className="mr-1.5"/> Copy
              </Button>
            )}
          </div>
          {!revealedSecret && (
            <form onSubmit={reveal} className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Your password (re-auth)</Label>
                <Input type="password" value={revealPassword}
                  onChange={(e) => setRevealPassword(e.target.value)}
                  placeholder="•••••••" className="mt-1 h-10 rounded-sm"
                  data-testid="sso-reveal-password"/>
              </div>
              <Button type="submit" disabled={revealing}
                className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-bold"
                data-testid="sso-reveal-btn">
                <Eye size={12} className="mr-1.5"/> {revealing ? "Revealing…" : "Reveal secret"}
              </Button>
            </form>
          )}
          {revealedSecret && (
            <Button size="sm" variant="outline" onClick={() => setRevealedSecret(null)}
              className="h-8 rounded-sm border-[#0B132B]/20" data-testid="sso-hide-btn">
              <EyeOff size={12} className="mr-1.5"/> Hide again
            </Button>
          )}
        </Card>

        {/* Commissions SSO secret card */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-commissions-secret-card">
          <div className="flex items-start gap-3 mb-4">
            <DollarSign size={22} className="text-[#0B132B]"/>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Commissions handoff secret (HS256)</div>
              <div className="text-base font-mono text-[#0B132B] mt-1">{revealedCommSecret || commissionsConfig?.secret_masked || "unset"}</div>
              <div className="text-[11px] text-[#6C757D] mt-1">Paste on the Commissions app as <span className="font-mono">SSO_PARENT_SECRET</span>. AWG mints tokens with this so users skip the second login when they open the Commissions tab.</div>
            </div>
            {revealedCommSecret && (
              <Button size="sm" variant="outline" onClick={() => copy(revealedCommSecret, "Commissions secret")}
                className="rounded-sm h-9 border-[#0B132B]/20" data-testid="copy-comm-secret">
                <Copy size={12} className="mr-1.5"/> Copy
              </Button>
            )}
          </div>
          {!revealedCommSecret ? (
            <form onSubmit={revealComm} className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Your password (re-auth)</Label>
                <Input type="password" value={revealCommPassword}
                  onChange={(e) => setRevealCommPassword(e.target.value)}
                  placeholder="•••••••" className="mt-1 h-10 rounded-sm"
                  data-testid="sso-reveal-comm-password"/>
              </div>
              <Button type="submit" disabled={revealingComm}
                className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-bold"
                data-testid="sso-reveal-comm-btn">
                <Eye size={12} className="mr-1.5"/> {revealingComm ? "Revealing…" : "Reveal commissions secret"}
              </Button>
            </form>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setRevealedCommSecret(null)}
              className="h-8 rounded-sm border-[#0B132B]/20" data-testid="sso-hide-comm-btn">
              <EyeOff size={12} className="mr-1.5"/> Hide again
            </Button>
          )}
        </Card>

        {/* Agency email-domain mapping */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-agency-map-card">
          <div className="flex items-start gap-3 mb-4">
            <Building2 size={22} className="text-[#0B132B]"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Auto-provision → agency</div>
              <div className="text-base font-bold text-[#0B132B]">Email-domain mapping</div>
              <div className="text-[11px] text-[#6C757D] mt-1">When a new SSO user arrives without an explicit <span className="font-mono">agency_id</span> claim, we match their email domain against the table below and drop them into that agency as an agent.</div>
            </div>
          </div>
          {agencies.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#6C757D] italic">No agencies yet. Add one from Admin → Agencies to configure a mapping.</div>
          ) : (
            <div className="border border-[#E9ECEF] rounded-sm overflow-hidden">
              <div className="grid grid-cols-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] p-3 border-b border-[#E9ECEF]">
                <div>Agency</div>
                <div>SSO email domain</div>
              </div>
              {agencies.map((a) => (
                <div key={a.id} className="grid grid-cols-2 p-3 border-b border-[#E9ECEF] last:border-b-0 text-sm" data-testid={`sso-agency-row-${a.id}`}>
                  <div className="font-semibold text-[#0B132B] truncate">{a.name}</div>
                  <div className="font-mono text-[#0B132B]">
                    {a.sso_email_domain ? `@${a.sso_email_domain}` : <span className="text-[#6C757D] italic font-sans">not set</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-[#6C757D] mt-3">Set / edit the domain from <span className="font-semibold">Admin → Agencies → Edit</span>.</div>
        </Card>

        {/* Integration snippets */}
        <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="sso-snippet-card">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-bold text-[#0B132B]">Parent-site integration</div>
            <a href="https://jwt.io" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] font-bold text-[#0B132B] hover:text-[#D4AF37]">
              <ExternalLink size={12}/> JWT debugger
            </a>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2">1) Iframe + postMessage (browser)</div>
          <pre className="text-[11px] bg-[#0B132B] text-[#F8F9FA] p-4 rounded-sm overflow-x-auto whitespace-pre" data-testid="sso-iframe-snippet">{iframeSnippet}</pre>
          <div className="mt-3 flex justify-end">
            <Button size="sm" variant="outline" onClick={() => copy(iframeSnippet, "Iframe snippet")}
              className="h-8 rounded-sm border-[#0B132B]/20" data-testid="sso-copy-iframe">
              <Copy size={12} className="mr-1.5"/> Copy iframe snippet
            </Button>
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2 mt-6">2) Mint the JWT server-side (Node example)</div>
          <pre className="text-[11px] bg-[#0B132B] text-[#F8F9FA] p-4 rounded-sm overflow-x-auto whitespace-pre" data-testid="sso-node-snippet">{nodeSnippet}</pre>
          <div className="mt-3 flex justify-end">
            <Button size="sm" variant="outline" onClick={() => copy(nodeSnippet, "Node snippet")}
              className="h-8 rounded-sm border-[#0B132B]/20" data-testid="sso-copy-node">
              <Copy size={12} className="mr-1.5"/> Copy Node snippet
            </Button>
          </div>

          <div className="mt-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37] rounded-sm text-[11px] text-[#0B132B]">
            <div className="font-bold uppercase tracking-[0.16em] text-[#0B132B] mb-1">JWT claims we accept</div>
            <ul className="space-y-0.5 leading-relaxed">
              <li>· <span className="font-mono">email</span> (required)</li>
              <li>· <span className="font-mono">name</span> — display name (defaults to email prefix)</li>
              <li>· <span className="font-mono">role</span> — <span className="font-mono">agent</span> / <span className="font-mono">agency_admin</span> / <span className="font-mono">admin</span></li>
              <li>· <span className="font-mono">agency_id</span> OR <span className="font-mono">agency_name</span> — else we match by email domain</li>
              <li>· <span className="font-mono">visibility_scope</span> — <span className="font-mono">all</span> / <span className="font-mono">assigned</span></li>
              <li>· <span className="font-mono">exp</span> — keep short (2–5 min). Tokens are single-use in practice.</li>
            </ul>
          </div>
        </Card>
      </main>
    </div>
  );
}
